import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Briefcase, CheckCircle2, DollarSign, FileText, Loader2, Paperclip, Send, User, X } from 'lucide-react';
import { safeFetch } from '../../lib/api';
import { notifyToast } from '../../lib/toastStore';
import Modal from '../shared/Modal';
import SearchableSelect from '../shared/SearchableSelect';
import {
  OFFER_PAY_CYCLES,
  OFFER_CURRENCIES,
  OFFER_COMPENSATION_REQUIRED_ERROR,
  formatOfferCompensation,
  isValidOfferCompensation,
  parseJobRateText,
} from '../../lib/offerCompensation';

/**
 * Issue an offer on behalf of an employer (Employer Phase 3 §7).
 *
 * Same two-step shape as the employer's OfferFormModal ("fill in the
 * details" → "Check and send"), posting to the admin endpoint that
 * runs the SAME issuance core: the applicant receives an offer from
 * the EMPLOYER (48 hours, same email + bell); the admin is recorded
 * as the issuing actor and the employer is notified afterwards.
 */

const ACCEPTED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const MAX_BYTES = 10 * 1024 * 1024;

interface AdminOfferOnBehalfModalProps {
  employers: Array<{ id: string; companyName: string }>;
  onClose: () => void;
  onDone: () => void;
}

export default function AdminOfferOnBehalfModal({ employers, onClose, onDone }: AdminOfferOnBehalfModalProps) {
  const [step, setStep] = useState(1);
  const [employerId, setEmployerId] = useState('');
  const [candidates, setCandidates] = useState<any[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [candidateId, setCandidateId] = useState('');
  const [details, setDetails] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  // Pay for the offer (REQUIRED — the shared issuance core refuses
  // an offer without it). Prefilled from the job posting's rate when
  // the candidate is picked; always editable.
  const [compAmount, setCompAmount] = useState('');
  const [compCurrency, setCompCurrency] = useState('CAD');
  const [compCycle, setCompCycle] = useState('');
  const [compError, setCompError] = useState<string | null>(null);

  const employer = employers.find((e) => e.id === employerId) || null;
  const candidate = candidates.find((c) => c.id === candidateId) || null;
  const compensationValue = { amount: Number(compAmount), currency: compCurrency, payCycle: compCycle };
  const compValid = compAmount.trim() !== '' && isValidOfferCompensation(compensationValue);

  // Picking a candidate prefills the pay from THAT candidate's job
  // posting rate (editable; required either way).
  useEffect(() => {
    if (!candidate) return;
    const prefill = parseJobRateText(candidate.jobCompensation || '');
    setCompAmount(prefill.amount != null ? String(prefill.amount) : '');
    setCompCurrency(prefill.currency || 'CAD');
    setCompCycle(prefill.payCycle || '');
    setCompError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateId]);

  const goToCheck = () => {
    if (!compValid) {
      setCompError(OFFER_COMPENSATION_REQUIRED_ERROR);
      return;
    }
    setCompError(null);
    setStep(2);
  };

  useEffect(() => {
    if (!employerId) { setCandidates([]); setCandidateId(''); return; }
    setCandidatesLoading(true);
    setCandidateId('');
    safeFetch(`/api/admin/offers/candidates?employerId=${encodeURIComponent(employerId)}`)
      .then((d: any) => setCandidates(d.candidates || []))
      .catch(() => setCandidates([]))
      .finally(() => setCandidatesLoading(false));
  }, [employerId]);

  const onPickFile = (picked: File | null) => {
    if (!picked) { setFile(null); return; }
    if (!ACCEPTED_TYPES.includes(picked.type)) {
      notifyToast('error', 'Please attach the offer letter as a PDF or Word document.');
      return;
    }
    if (picked.size > MAX_BYTES) {
      notifyToast('error', 'The offer letter is too large — please attach a file under 10 MB.');
      return;
    }
    setFile(picked);
  };

  const readAttachment = (): Promise<any | undefined> => new Promise((resolve, reject) => {
    if (!file) { resolve(undefined); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      resolve({ name: file.name, mimeType: file.type, base64Data: result.includes(',') ? result.split(',')[1] : result });
    };
    reader.onerror = () => reject(new Error('The attachment could not be read.'));
    reader.readAsDataURL(file);
  });

  const send = async () => {
    if (!employer || !candidate) return;
    setSending(true);
    try {
      const attachment = await readAttachment();
      const d: any = await safeFetch('/api/admin/offers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employerId: employer.id,
          applicationId: candidate.id,
          details: details.trim() || undefined,
          compensation: { amount: Number(compAmount), currency: compCurrency, payCycle: compCycle },
          attachment,
        }),
      });
      notifyToast('success', `Offer ${d?.offer?.offerRef || ''} sent to ${candidate.applicantName || 'the candidate'} — the employer has been notified.`);
      onDone();
    } catch (err: any) {
      notifyToast('error', err?.message || 'The offer could not be sent.');
    } finally {
      setSending(false);
    }
  };

  const candidateBlocked = (c: any): string | null => {
    if (c.liveOfferRef) return `Offer ${c.liveOfferRef} is already waiting for a response`;
    if (c.status === 'hired') return 'Already hired for a job';
    if (c.status === 'assigned') return 'Already assigned';
    return null;
  };

  return (
    <Modal onClose={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[88vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-black text-slate-900 flex items-center gap-2">
              <Send className="h-5 w-5 text-emerald-600" />
              Issue an offer on behalf of an employer
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              {step === 1 ? 'Choose the employer and candidate, then add anything extra.' : 'Check everything below, then send.'}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100 text-slate-400 cursor-pointer" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label htmlFor="obh-employer" className="block text-sm font-bold text-slate-700 mb-1.5">Employer</label>
              <SearchableSelect
                id="obh-employer"
                value={employerId}
                onChange={(v) => setEmployerId(v)}
                options={employers.map((e) => ({ value: e.id, label: e.companyName }))}
                placeholder="Search employers…"
                clearLabel="Choose the employer…"
                emptyLabel="No employers found"
              />
            </div>

            {employerId && (
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1.5">Candidate shared with this employer</label>
                {candidatesLoading ? (
                  <p className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading shared candidates…</p>
                ) : candidates.length === 0 ? (
                  <p className="text-sm text-slate-500">No candidates are shared with this employer yet. Share a candidate with them first, then come back.</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                    {candidates.map((c) => {
                      const blocked = candidateBlocked(c);
                      const selected = candidateId === c.id;
                      return (
                        <button
                          key={c.id}
                          type="button"
                          disabled={!!blocked}
                          onClick={() => setCandidateId(c.id)}
                          className={`w-full text-left rounded-xl border p-3 transition ${selected ? 'border-emerald-500 bg-emerald-50/60' : 'border-slate-200 bg-white hover:bg-slate-50'} ${blocked ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          <span className="flex items-center gap-2 text-sm font-bold text-slate-800">
                            <User className="h-4 w-4 text-slate-400" /> {c.applicantName || 'Candidate'}
                          </span>
                          <span className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                            <Briefcase className="h-3.5 w-3.5 text-slate-400" /> {c.jobTitle || 'Job'}{c.programType ? ` · ${c.programType}` : ''}
                          </span>
                          {blocked && <span className="block text-xs font-semibold text-amber-600 mt-1">{blocked}</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {candidate && (
              <div className="rounded-2xl border border-slate-200 p-4 space-y-3">
                <span className="flex items-center gap-1.5 text-sm font-bold text-slate-700">
                  <DollarSign className="h-4 w-4 text-emerald-600" />
                  Pay for this offer <span className="text-red-500">(required)</span>
                </span>
                <div className="grid grid-cols-[1fr_110px] gap-2">
                  <div>
                    <label htmlFor="obh-pay-amount" className="block text-xs font-bold text-slate-500 mb-1">Amount</label>
                    <input
                      id="obh-pay-amount"
                      type="number"
                      min="0"
                      step="0.01"
                      inputMode="decimal"
                      value={compAmount}
                      onChange={(e) => { setCompAmount(e.target.value); setCompError(null); }}
                      placeholder="e.g. 3200"
                      className="w-full border border-slate-300 rounded-xl px-3 py-2.5 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-brand-primary/30"
                    />
                  </div>
                  <div>
                    <label htmlFor="obh-pay-currency" className="block text-xs font-bold text-slate-500 mb-1">Currency</label>
                    <select
                      id="obh-pay-currency"
                      value={compCurrency}
                      onChange={(e) => { setCompCurrency(e.target.value); setCompError(null); }}
                      className="w-full border border-slate-300 rounded-xl px-2 py-2.5 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-brand-primary/30 bg-white"
                    >
                      {OFFER_CURRENCIES.map((c) => (<option key={c} value={c}>{c}</option>))}
                    </select>
                  </div>
                </div>
                <div>
                  <label htmlFor="obh-pay-cycle" className="block text-xs font-bold text-slate-500 mb-1">Paid</label>
                  <select
                    id="obh-pay-cycle"
                    value={compCycle}
                    onChange={(e) => { setCompCycle(e.target.value); setCompError(null); }}
                    className="w-full border border-slate-300 rounded-xl px-2 py-2.5 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-brand-primary/30 bg-white"
                  >
                    <option value="">Choose how often…</option>
                    {OFFER_PAY_CYCLES.map((c) => (<option key={c} value={c}>{c}</option>))}
                  </select>
                </div>
                {compValid ? (
                  <p className="text-xs font-semibold text-emerald-700">The offer will state: <strong>{formatOfferCompensation(compensationValue as any)}</strong>.</p>
                ) : (
                  <p className="text-xs text-slate-500">The candidate sees this pay on their offer. It can be different from the pay on the job posting — if the posting showed a rate, we filled it in to start; please check it.</p>
                )}
                {compError && (
                  <div className="px-3 py-2.5 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-700 font-semibold">{compError}</div>
                )}
              </div>
            )}

            <div>
              <label htmlFor="obh-details" className="block text-sm font-bold text-slate-700 mb-1.5">
                Anything extra for the candidate? <span className="font-semibold text-slate-400">(optional)</span>
              </label>
              <textarea
                id="obh-details"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                rows={3}
                placeholder="For example: start date, salary, or anything the candidate should know."
                className="w-full border border-slate-300 rounded-xl px-3 py-2.5 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-brand-primary/30 resize-none"
              />
            </div>

            <div>
              <span className="block text-sm font-bold text-slate-700 mb-1.5">
                Offer letter <span className="font-semibold text-slate-400">(optional — PDF or Word, up to 10 MB)</span>
              </span>
              {file ? (
                <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                  <FileText className="h-4 w-4 text-slate-500 shrink-0" />
                  <span className="text-sm text-slate-700 truncate">{file.name}</span>
                  <button type="button" onClick={() => setFile(null)} className="ml-auto text-xs font-bold text-red-600 hover:text-red-700 cursor-pointer shrink-0">Remove</button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="w-full flex items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 px-3 py-3 text-sm font-bold text-slate-600 hover:bg-slate-50 cursor-pointer transition"
                >
                  <Paperclip className="h-4 w-4" /> Attach the offer letter
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                className="hidden"
                onChange={(e) => onPickFile(e.target.files?.[0] || null)}
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button onClick={onClose} className="px-4 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition cursor-pointer">Cancel</button>
              <button
                onClick={goToCheck}
                disabled={!employer || !candidate}
                className="px-5 py-2.5 text-sm font-bold text-white bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] rounded-xl transition cursor-pointer flex items-center gap-2 disabled:opacity-50"
              >
                Continue <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {step === 2 && employer && candidate && (
          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-2.5">
              {[
                ['Candidate', candidate.applicantName || '—'],
                ['Job', candidate.jobTitle || '—'],
                ['Offer from (employer)', employer.companyName],
                ['Pay', compValid ? formatOfferCompensation(compensationValue as any) : '—'],
                ['Issued by', 'You — ISATS admin, on behalf of the employer'],
                ['Offer letter', file ? file.name : 'None attached'],
              ].map(([label, value]) => (
                <div key={label} className="flex items-baseline justify-between gap-4">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-400 shrink-0">{label}</span>
                  <span className="text-sm font-bold text-slate-800 text-right break-words">{value}</span>
                </div>
              ))}
              {details.trim() && (
                <div className="pt-1">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Message for the candidate</span>
                  <p className="text-sm text-slate-700 mt-1 whitespace-pre-line">{details.trim()}</p>
                </div>
              )}
            </div>
            <p className="text-sm text-slate-600">
              {candidate.applicantName || 'The candidate'} will get an email and a portal notification right away, naming <strong>{employer.companyName}</strong> as the employer making the offer.
              They will have <strong>48 hours (two days)</strong> to accept or decline, and an expired offer returns them to their previous stage automatically.
              The employer is also notified that an offer was issued on their behalf.
            </p>
            <div className="flex items-center justify-between pt-2">
              <button onClick={() => setStep(1)} disabled={sending} className="px-4 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition cursor-pointer flex items-center gap-2 disabled:opacity-50">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              <button
                onClick={send}
                disabled={sending}
                className="px-5 py-2.5 text-sm font-bold text-white bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] rounded-xl transition cursor-pointer flex items-center gap-2 disabled:opacity-50"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                {sending ? 'Sending…' : 'Send the offer'}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
